/*
  C Product: gallery (thumbs <-> scroll-snap track, inline video), selling-plan picker and
  add to cart (subscription + optional add-on in one /cart/add.js request).
*/
(() => {
  const initGallery = (root) => {
    const track = root.querySelector('[data-c-product-track]');
    if (!track) return;
    const slides = [...track.querySelectorAll('[data-c-product-slide]')];
    const thumbs = [...root.querySelectorAll('[data-c-product-thumb]')];

    const setCurrent = (index) => {
      thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = Number(thumb.dataset.cProductThumb);
        track.scrollTo({ left: slides[index].offsetLeft - track.offsetLeft, behavior: 'smooth' });
        setCurrent(index);
      });
    });

    let ticking = false;
    track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setCurrent(Math.round(track.scrollLeft / track.clientWidth));
        ticking = false;
      });
    });

    // Pause any playing video when it scrolls out of view
    track.addEventListener('scrollend', () => {
      track.querySelectorAll('video').forEach((video) => {
        const slide = video.closest('[data-c-product-slide]');
        if (Math.abs(slide.offsetLeft - track.offsetLeft - track.scrollLeft) > 10) video.pause();
      });
    });

    root.querySelectorAll('[data-c-product-play]').forEach((button) => {
      button.addEventListener('click', () => {
        const slide = button.closest('[data-c-product-slide]');
        const template = slide.querySelector('template[data-c-product-video]');
        if (!template) return;
        slide.querySelector('img')?.remove();
        slide.appendChild(template.content.cloneNode(true));
        template.remove();
        slide.classList.add('is-playing');
        slide.querySelector('video')?.play();
      });
    });
  };

  const initForm = (form) => {
    const planInput = form.querySelector('[data-c-product-plan-input]');
    const variantInput = form.querySelector('[data-c-product-variant]');
    const addonCards = [...form.querySelectorAll('[data-c-product-addon-card]')];
    const button = form.querySelector('[data-c-product-atc]');
    const error = form.querySelector('[data-c-product-error]');
    const total = form.querySelector('[data-c-product-total]');

    // "Total today": the selected plan's charge plus the add-on when it is ticked
    const updateTotal = () => {
      if (!total) return;
      const plan = form.querySelector('[data-c-product-plan]:checked');
      const addon = addonCards.find((card) => !card.hidden)?.querySelector('[data-c-product-addon]');
      let cents = Number(plan?.dataset.price || 0);
      if (addon && addon.checked) cents += Number(addon.dataset.price || 0);
      total.querySelector('[data-c-product-total-value]').textContent = new Intl.NumberFormat(
        document.documentElement.lang || 'en',
        { style: 'currency', currency: total.dataset.currency || 'USD' }
      ).format(cents / 100);
    };

    form.querySelectorAll('[data-c-product-plan]').forEach((radio) => {
      radio.addEventListener('change', () => {
        if (!radio.checked) return;
        planInput.value = radio.value;
        // Each plan has its own add-on (12 / 6 / 3 guides); the others hide and untick
        addonCards.forEach((card) => {
          const match = card.dataset.cProductAddonCard === radio.value;
          card.hidden = !match;
          if (!match) card.querySelector('[data-c-product-addon]').checked = false;
        });
        updateTotal();
      });
    });

    form.querySelectorAll('[data-c-product-addon]').forEach((addon) => addon.addEventListener('change', updateTotal));
    updateTotal();

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (button.disabled) return;
      error.hidden = true;
      button.setAttribute('aria-busy', 'true');

      const items = [{ id: Number(variantInput.value), quantity: 1 }];
      if (planInput.value) items[0].selling_plan = Number(planInput.value);
      const addon = addonCards.find((card) => !card.hidden)?.querySelector('[data-c-product-addon]');
      if (addon && addon.checked) items.push({ id: Number(addon.value), quantity: 1 });

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Could not add to cart');
        window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
      } catch (e) {
        error.textContent = e.message;
        error.hidden = false;
        button.removeAttribute('aria-busy');
      }
    });
  };

  // Mouse drag-to-scroll for horizontal rails (touch already scrolls natively).
  // A drag of more than 5px swallows the click that follows, so links don't fire.
  const initDrag = (rail) => {
    let startX = 0;
    let startScroll = 0;
    let dragging = false;
    let moved = false;

    rail.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      dragging = true;
      moved = false;
      startX = event.clientX;
      startScroll = rail.scrollLeft;
    });

    rail.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const dx = event.clientX - startX;
      if (!moved && Math.abs(dx) > 5) {
        moved = true;
        rail.classList.add('is-dragging');
        rail.setPointerCapture(event.pointerId);
      }
      if (moved) rail.scrollLeft = startScroll - dx;
    });

    const end = () => {
      if (!dragging) return;
      dragging = false;
      rail.classList.remove('is-dragging');
    };
    rail.addEventListener('pointerup', end);
    rail.addEventListener('pointercancel', end);
    rail.addEventListener('lostpointercapture', end);

    rail.addEventListener(
      'click',
      (event) => {
        if (moved) {
          event.preventDefault();
          event.stopPropagation();
          moved = false;
        }
      },
      true
    );
  };

  const init = () => {
    document.querySelectorAll('.c-product [data-c-drag]:not([data-ready])').forEach((rail) => {
      rail.dataset.ready = '1';
      initDrag(rail);
    });
    document.querySelectorAll('[data-c-product-gallery]:not([data-ready])').forEach((root) => {
      root.dataset.ready = '1';
      initGallery(root);
    });
    document.querySelectorAll('[data-c-product-form]:not([data-ready])').forEach((form) => {
      form.dataset.ready = '1';
      initForm(form);
    });
  };

  init();
  document.addEventListener('shopify:section:load', init);
})();
